import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Destination2 } from '~/models/Destination'
import { Itinerary2 } from '~/models/Itinerary'
import Media from '~/models/Media'
import { Page } from '~/models/PaginatedItems'
import { Spot2 } from '~/models/Spot'

export const useItineraryStore = defineStore('itinerary', () => {
	const itineraries = ref<Record<string, Itinerary2>>({})
	const spots = ref<Record<string, any>>({})
	const spotMedias = ref<Record<string, Media[]>>({})
	const destinations = ref<Record<string, Destination2[]>>({})
	const recommendedSpots = ref<Record<string, Spot2[]>>({})
	const tripUserMedias = ref<Record<string, Media[]>>({})

	const addItinerary = (itinerary: Itinerary2) => {
		itineraries.value[itinerary.id!] = itinerary
	}

	const removeItinerary = (itineraryId: string) => {
		itineraries.value[itineraryId] = undefined as any
	}

	const retrieveItinerary = (itineraryId: string): Itinerary2 | undefined => {
		return itineraries.value[itineraryId]
	}

	const addSpotsTo = (
		itineraryId: string,
		page: number,
		pagedSpots: Page<Spot2>,
		group: string = 'all'
	): void => {
		if (!spots.value[itineraryId]) spots.value[itineraryId] = { groups: {} }
		const storedSpots = spots.value[itineraryId].groups[group]?.spots || []
		spots.value[itineraryId].groups[group] = {
			spots: [...storedSpots, ...pagedSpots.items],
			currentPage: page,
			total: pagedSpots.count,
		}
	}

	const resetSpots = (itineraryId: string): void => {
		spots.value[itineraryId] = { groups: {} }
	}

	const retrieveSpots = (itineraryId: string, group: string = 'all'): any | undefined => {
		if (!spots.value[itineraryId]) return undefined
		return spots.value[itineraryId].groups[group]
	}

	const addDestinationsTo = (itineraryId: string, destinationsToStore: Destination2[]): void => {
		destinations.value[itineraryId] = destinationsToStore
	}

	const retrieveDestinations = (itineraryId: string): Destination2[] | undefined => {
		return destinations.value[itineraryId]
	}

	const addSpotMediasTo = (spotId: string, page: number, mediasToStore: Media[]): void => {
		spotMedias.value[buildMediasByPageKey(spotId, page)] = mediasToStore
	}

	const retrieveSpotMedias = (spotId: string, page: number): Media[] | undefined => {
		return spotMedias.value[buildMediasByPageKey(spotId, page)]
	}

	const buildMediasByPageKey = (spotId: string, page: number): string => {
		return `${spotId}-page-${page}`
	}

	const retrieveRecommendedSpots = (itineraryId: string): Spot2[] | undefined => {
		return recommendedSpots.value[itineraryId]
	}

	const addRecommendedSpots = (itineraryId: string, spots: Spot2[]): void => {
		recommendedSpots.value[itineraryId] = spots
	}

	const retrieveMedias = (itineraryId: string): Media[] | undefined => {
		return tripUserMedias.value[itineraryId]
	}

	const resetItineraryMedias = (itineraryId: string): void => {
		delete tripUserMedias.value[itineraryId]
	}

	const addItineraryMedias = (itineraryId: string, medias: Media[]): void => {
		tripUserMedias.value[itineraryId] = medias
	}

	return {
		addItinerary,
		retrieveItinerary,
		addSpotsTo,
		resetSpots,
		retrieveSpots,
		addDestinationsTo,
		retrieveDestinations,
		addSpotMediasTo,
		retrieveSpotMedias,
		retrieveRecommendedSpots,
		addRecommendedSpots,
		retrieveMedias,
		addItineraryMedias,
		resetItineraryMedias,
		removeItinerary,
	}
})
